// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// main.ts — mount, wire the screens, own no rules.
//
// rAF IS A RENDER SIGNAL, NEVER A CLOCK. This is a turn-based game, so nothing here advances state on
// a frame: the loop only tweens what is already true. A backgrounded tab therefore cannot desynchronise
// anything, and the board reflows off a setTimeout rather than a frame, because a hidden tab reports a
// clientWidth of 0 and every piece piles into the corner.

import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';
import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { CELLS, SLOTS, SLUGS_CARRIED, VERB_SLOTS, Verb, MIN_THROW, SLUG_RANGE } from './tuning';
import { chebyshev, canStep, hasLos, neighbours, walkField } from './map';
import { DEFAULT_MODE, MODES, MODE_IDS, modeOf } from './modes';
import { generate, type Floorplan } from './gen';
import {
  band,
  bankedValue,
  canDo,
  commitTurn,
  createState,
  scoreOf,
  slotsOf,
  threatMask,
  validExits,
  type Action,
  type State,
} from './rules';
import { CUES, PATCHES } from './cues';
import { cellAt, draw, emptyView, fit, type Geometry, type View } from './render';
import { STEPS, allowed, setup as tutorialSetup } from './tutorial';
import { ABOUT, HOW_TO, bestLine, closeModal, el, modalArmed, modalOpen, openModal, paintHeat, paintRisk, paintSlots, paintTurn, setMode, setPosture, shell, showScreen } from './ui';
import { fromLocation, linkFor, record, replay, type Ghost } from './ghost';

const store = createStore('nightshift');
const sfx = createSfx({ patches: PATCHES, muted: store.get('muted', false) as boolean });

let mode = modeOf(store.get('mode', DEFAULT_MODE) as string);
let plan: Floorplan | null = null;
let s: State | null = null;
let staged: Action[] = [];
let log: Action[] = [];
let view: View = emptyView();
let geom: Geometry | null = null;
let teaching = false;
let teachStep = 0;
let slugArmed = false;
let shake = 0;
let ghost: Ghost | null = null;

const app = document.getElementById('app')!;
app.innerHTML = shell();
hardenViewport();

const canvas = el<HTMLCanvasElement>('#board');
const wrap = el<HTMLElement>('#board-wrap');
const ctx = canvas.getContext('2d')!;

// ── layout ──────────────────────────────────────────────────────────────────

/**
 * Size the board from a MEASURED container.
 *
 * Not from a formula against screen height — that is what put 43px cells on the reference phone and
 * pushed the commit bar off an iPhone SE entirely. A transient 0-size measurement is ignored and
 * retried rather than turned into a NaN scale that silently drops every tap.
 */
function resize(): void {
  // Measured off the CANVAS, which `inset: 0` pins to the container — so this is the space the board
  // actually has rather than the space it had when the layout last looked different.
  const r = canvas.getBoundingClientRect();
  const g = fit(r.width, r.height);
  if (!g) {
    setTimeout(resize, 60);
    return;
  }
  geom = g;
  // Only the BACKING STORE is set here. Writing canvas.style.width as well fights `inset: 0` and wins,
  // which pinned the element at the 300x150 intrinsic default and then fed that measurement straight
  // back in on the next call — a layout that measures its own mistake.
  const dpr = Math.min(3, window.devicePixelRatio || 1);
  canvas.width = Math.round(r.width * dpr);
  canvas.height = Math.round(r.height * dpr);
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  paint();
}
window.addEventListener('resize', () => setTimeout(resize, 0));
window.addEventListener('orientationchange', () => setTimeout(resize, 120));
// The tutorial panel appearing changes how much room the board has, and so does the landscape rail.
// A ResizeObserver is the only thing that hears either of those.
new ResizeObserver(() => resize()).observe(wrap);

// ── painting ────────────────────────────────────────────────────────────────

const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function paint(): void {
  if (!s || !geom) return;
  draw(ctx, geom, s, view, reduced ? 0 : shake);
  paintHeat(s.heat, s.floorHeat, band(s));
  paintTurn(s.turn, s.mode.turns, bankedValue(s), s.carrying.length, s.slugs);
  paintSlots(slotsOf(staged));
  const worst = view.routeRisk.length ? Math.max(...view.routeRisk) : 0;
  paintRisk(worst, noteFor(s));
}

function noteFor(st: State): string {
  if (slugArmed) return 'pick a cell at least four away';
  if (st.inDuct) return 'in the duct — nobody can see or hear you, and nothing is cooling down';
  const carrying = st.carrying.length;
  if (carrying > 1) return `${carrying} in hand — every step is louder`;
  return '';
}

/** The one rAF in the game, and it only decays a shake. Nothing here advances state. */
function frame(): void {
  if (shake > 0.1) {
    shake *= 0.86;
    paint();
  } else if (shake !== 0) {
    shake = 0;
    paint();
  }
  requestAnimationFrame(frame);
}
requestAnimationFrame(frame);

// ── staging a turn ──────────────────────────────────────────────────────────

const stagedPos = (): number => {
  let p = s!.pos;
  for (const a of staged) if (a.verb === Verb.Step || a.verb === Verb.Creep || a.verb === Verb.Bolt) p = a.arg;
  return p;
};

/** Rebuild the drawn route and its per-cell risk from whatever is staged. */
function refreshView(): void {
  if (!s) return;
  const now = threatMask(s, s.posture);
  const soon = threatMask(s, s.posture, true);
  const route: number[] = [];
  const risk: number[] = [];
  for (const a of staged) {
    if (a.verb !== Verb.Step && a.verb !== Verb.Creep && a.verb !== Verb.Bolt) continue;
    route.push(a.arg);
    risk.push(now[a.arg] ? 2 : soon[a.arg] ? 1 : 0);
  }
  view = { ...view, route, routeRisk: risk, slugCells: slugArmed ? slugTargets() : [] };
  if (teaching) view.hint = STEPS[teachStep] ? STEPS[teachStep].hint(s) : -1;
  else view.hint = -1;
  paint();
}

function slugTargets(): number[] {
  if (!s) return [];
  const from = stagedPos();
  const out: number[] = [];
  for (let c = 0; c < CELLS; c++) {
    const d = chebyshev(from, c);
    if (d < MIN_THROW || d > SLUG_RANGE) continue;
    if (!canStep(s.grid, s.doors, c) || !hasLos(s.grid, s.doors, from, c)) continue;
    out.push(c);
  }
  return out;
}

/** Stage the cheapest legal route from where the staged turn currently ends to `target`. */
function routeTo(target: number): void {
  if (!s) return;
  const from = stagedPos();
  if (from === target) return;
  const field = walkField(s.grid, s.doors, target);
  if (field[from] < 0) {
    // Not walkable from here with the doors as they stand — offer the door itself instead.
    const nb: number[] = [];
    neighbours(from, nb);
    if (nb.includes(target)) tryStage({ verb: doorVerbFor(target) ?? Verb.Step, arg: target });
    else sfx.play('deny');
    return;
  }
  let at = from;
  const nb: number[] = [];
  for (let guard = 0; guard < CELLS && at !== target; guard++) {
    neighbours(at, nb);
    const next = nb.filter((c) => field[c] >= 0 && field[c] < field[at]).sort((a, b) => a - b)[0];
    if (next === undefined) break;
    if (!tryStage({ verb: s.posture, arg: next })) break;
    at = next;
  }
}

function doorVerbFor(cell: number): Verb | null {
  if (!s) return null;
  const d = s.grid.doorAt[cell];
  if (d < 0) return null;
  for (const v of [Verb.OpenDoor, Verb.ForceDoor]) if (canDo(s, { verb: v, arg: cell }, slotsOf(staged))) return v;
  return null;
}

function tryStage(a: Action): boolean {
  if (!s) return false;
  const probe = probeState();
  if (!canDo(probe, a, slotsOf(staged))) return false;
  if (teaching && !allowed(teachStep, probe, a)) {
    refuse();
    return false;
  }
  staged.push(a);
  // A step whose whole point is the PREVIEW is finished the moment the route is staged, not when the
  // turn is committed — otherwise "tap the case" sits there having plainly already happened.
  advanceTeaching();
  refreshView();
  return true;
}

/** A shallow state advanced by whatever is already staged, so legality is asked about the right cell. */
function probeState(): State {
  const p = { ...s!, doors: s!.doors.map((d) => ({ ...d })) } as State;
  for (const a of staged) {
    if (a.verb === Verb.Step || a.verb === Verb.Creep || a.verb === Verb.Bolt) p.pos = a.arg;
    else if (a.verb === Verb.OpenDoor || a.verb === Verb.ForceDoor) p.doors[p.grid.doorAt[a.arg]].state = 0;
    else if (a.verb === Verb.CloseDoor) p.doors[p.grid.doorAt[a.arg]].state = 1;
  }
  return p;
}

function refuse(): void {
  sfx.play('deny');
  const step = STEPS[teachStep];
  if (step) el('#teach-text').textContent = step.refusal;
}

/**
 * The geometry to HIT-TEST against, computed from the live rect at the moment of the touch.
 *
 * Deliberately not the cached `geom` the renderer uses. Any layout change that has not yet been
 * observed — the tutorial panel appearing, the landscape rail, a rotation — leaves that cache
 * describing a box the board no longer occupies, and the whole column of taps lands one row out. It is
 * the same class of bug as hit-testing inside the draw loop: input geometry must never depend on when
 * the last frame happened to be measured. `fit` is a few divisions, so measuring per touch is free.
 */
function hitGeom(): { g: Geometry; rect: DOMRect } | null {
  const rect = canvas.getBoundingClientRect();
  const g = fit(rect.width, rect.height);
  return g ? { g, rect } : null;
}

// ── the board's gestures ────────────────────────────────────────────────────
//
// Tap a cell: if it is an object you are next to, that verb; otherwise a route to it. Dragging from
// your own token extends the same route along the finger. No D-pad and no analog stick — this is a
// grid game and it gets grid gestures.

let dragging = false;

canvas.addEventListener('pointerdown', (e) => {
  if (!s || s.over) return;
  const hit = hitGeom();
  if (!hit) return;
  canvas.setPointerCapture(e.pointerId);
  const c = cellAt(hit.g, hit.rect, e.clientX, e.clientY);
  if (c < 0) return;
  dragging = c === stagedPos();
  handleCell(c);
});
canvas.addEventListener('pointermove', (e) => {
  if (!dragging || !s) return;
  const hit = hitGeom();
  if (!hit) return;
  const c = cellAt(hit.g, hit.rect, e.clientX, e.clientY);
  if (c >= 0 && c !== stagedPos()) routeTo(c);
});
const endDrag = (): void => {
  dragging = false;
};
canvas.addEventListener('pointerup', endDrag);
// An incoming call fires pointercancel, not pointerup. Treated as an aborted gesture, not a drop.
canvas.addEventListener('pointercancel', endDrag);

function handleCell(c: number): void {
  if (!s) return;
  if (slugArmed) {
    const a: Action = { verb: Verb.Slug, arg: c };
    if (tryStage(a)) {
      slugArmed = false;
      sfx.play('select');
    } else sfx.play('deny');
    refreshView();
    return;
  }
  const probe = probeState();
  for (const v of [Verb.Grab, Verb.Bank, Verb.Breaker, Verb.OpenDoor, Verb.CloseDoor, Verb.ForceDoor]) {
    if (canDo(probe, { verb: v, arg: c }, slotsOf(staged))) {
      if (tryStage({ verb: v, arg: c })) sfx.play('select');
      return;
    }
  }
  if (s.grid.ductAt[c] >= 0 && c === stagedPos() && canDo(probe, { verb: Verb.Duct, arg: 0 }, slotsOf(staged))) {
    tryStage({ verb: Verb.Duct, arg: 0 });
    return;
  }
  routeTo(c);
}

// ── committing ──────────────────────────────────────────────────────────────

function commit(): void {
  if (!s || s.over) return;
  if (teaching) {
    const step = STEPS[teachStep];
    if (step && !step.done(s, staged) && staged.length === 0) {
      refuse();
      return;
    }
  }
  const before = { heat: s.heat, seen: s.perceivedEver, band: band(s), marks: s.marks.length };
  log.push(...staged, { verb: Verb.Hold, arg: 0 });
  for (const a of staged) soundFor(a);
  commitTurn(s, staged);
  staged = [];
  slugArmed = false;

  if (s.perceivedEver && !before.seen) {
    sfx.play('spot');
    shake = 9;
  }
  if (s.marks.length < before.marks) sfx.play('read');
  if (band(s) > before.band) sfx.play(band(s) >= 3 ? 'ratchet' : 'band');
  advanceTeaching();
  refreshView();
  if (s.over) finish();
}

function soundFor(a: Action): void {
  switch (a.verb) {
    case Verb.Step:
      sfx.play('step');
      break;
    case Verb.Creep:
      sfx.play('creep');
      break;
    case Verb.Bolt:
      sfx.play('bolt');
      break;
    case Verb.OpenDoor:
      sfx.play('door');
      break;
    case Verb.CloseDoor:
      sfx.play('shut');
      break;
    case Verb.ForceDoor:
      sfx.play('force');
      break;
    case Verb.Breaker:
      sfx.play('breaker');
      break;
    case Verb.Slug:
      sfx.play('slug');
      break;
    case Verb.Grab:
      sfx.play('grab');
      break;
    case Verb.Bank:
      sfx.play('bank');
      break;
    case Verb.Duct:
      sfx.play('duct');
      break;
    default:
      break;
  }
}

function advanceTeaching(): void {
  if (!teaching || !s) return;
  while (STEPS[teachStep] && STEPS[teachStep].done(s, staged)) {
    el('#teach-text').textContent = STEPS[teachStep].after;
    teachStep++;
    if (teachStep >= STEPS.length) {
      teaching = false;
      store.set('taught', true);
      el<HTMLElement>('#teach').hidden = true;
      sfx.play('out');
      return;
    }
  }
  paintTeach();
}

function paintTeach(): void {
  const step = STEPS[teachStep];
  if (!step) return;
  const was = el<HTMLElement>('#teach').hidden;
  el<HTMLElement>('#teach').hidden = false;
  el('#teach-text').textContent = step.text;
  el('#teach-step').textContent = `${teachStep + 1} / ${STEPS.length}`;
  // Showing the panel takes height off the board, so the backing store has to follow it.
  if (was) setTimeout(resize, 0);
}

// ── starting and finishing ──────────────────────────────────────────────────

function startRun(m = mode, seed = (Math.random() * 0x7fffffff) | 0): void {
  const found = generate(seed, m);
  if (!found) {
    openModal('That floor would not build', '<p>Try again — a different building is one tap away.</p>');
    return;
  }
  plan = found;
  s = createState(found.b, m, seed);
  staged = [];
  log = [];
  teaching = false;
  view = emptyView();
  showScreen('game');
  el<HTMLElement>('#teach').hidden = true;
  setPosture('walk');
  resize();
  refreshView();
}

function startTutorial(): void {
  s = tutorialSetup();
  plan = null;
  staged = [];
  log = [];
  teaching = true;
  teachStep = 0;
  view = emptyView();
  showScreen('game');
  setPosture('walk');
  resize();
  paintTeach();
  refreshView();
}

function finish(): void {
  if (!s) return;
  const score = scoreOf(s);
  sfx.play(s.outcome === 'escaped' ? 'out' : 'lost');
  const key = `best:${s.mode.id}`;
  if (score > (store.get(key, 0) as number)) store.set(key, score);

  el('#verdict').textContent =
    s.outcome === 'escaped' ? 'Out, with the goods' : s.outcome === 'caught' ? 'Caught' : 'The shift ended';
  const par = plan?.verdict.par ?? 0;
  el('#summary').textContent = par
    ? `${score} against a par of ${par} — par took ${plan!.verdict.parTurns} turns and ${plan!.verdict.forcedRisk} held breaths.`
    : `${score}.`;

  // EVERY outcome is shown, not just the score: what you banked, what you were still holding, what the
  // building took back, and what you never found. This is the one moment a player compares themselves.
  const rows: string[] = [];
  for (const it of s.items) {
    const where = it.banked
      ? 'banked'
      : it.confiscated
        ? 'taken back by the custodian'
        : s.carrying.includes(it.id)
          ? 'still in your hands'
          : 'never found';
    rows.push(`<li><b>${it.value}</b><span>${where}</span></li>`);
  }
  const missed = s.items.filter((i) => !i.banked).reduce((v, i) => v + i.value, 0);
  rows.push(
    `<li class="tot"><b>${bankedValue(s)}</b><span>banked${missed ? ` · ${missed} left in the building` : ''}</span></li>`,
  );
  el('#ledger').innerHTML = `<ul>${rows.join('')}</ul>`;
  if (plan) view = { ...view, par: plan.verdict.route };
  showScreen('results');
}

// ── shared buildings ────────────────────────────────────────────────────────

function share(): void {
  if (!s || !plan) return;
  const g = record(s.seed, s.mode.id, log, scoreOf(s));
  const url = linkFor(g);
  navigator
    .share?.({ title: 'Nightshift', text: `I got ${scoreOf(s)} out of this building.`, url })
    .catch(() => copy(url)) ?? copy(url);
}

function copy(url: string): void {
  navigator.clipboard
    ?.writeText(url)
    .then(() => openModal('Link copied', '<p>Send it to somebody. They get the same building and your route drawn over theirs.</p>'))
    .catch(() => openModal('Here is the link', `<p class="link-out">${url}</p>`));
}

function openShared(g: Ghost): void {
  const verdict = replay(g);
  ghost = g;
  if (verdict.kind === 'bad') {
    openModal('That link would not replay', `<p>${verdict.why}. Starting a fresh building instead.</p>`);
    startRun();
    return;
  }
  mode = modeOf(g.mode);
  setMode(mode.id);
  startRun(mode, g.seed);
  if (s) view = { ...view, par: routeOf(verdict.kind === 'ok' ? verdict.state : null) };
  openModal(
    verdict.kind === 'stale' ? 'A run from an older build' : 'Somebody else’s night',
    verdict.kind === 'stale'
      ? '<p>This route was recorded before the rules last changed, so their score is not comparable — but it is the same building, and their route is drawn on it.</p>'
      : `<p>They scored <b>${verdict.score}</b> here. Same floor, same patrols. Their route is drawn behind yours.</p>`,
  );
  refreshView();
}

const routeOf = (st: State | null): number[] => (st ? [] : []);

// ── wiring ──────────────────────────────────────────────────────────────────

const on = (sel: string, fn: (e: Event) => void): void => el(sel).addEventListener('click', fn);

for (const b of document.querySelectorAll<HTMLElement>('.mode')) {
  b.addEventListener('click', () => {
    mode = modeOf(b.dataset.mode);
    store.set('mode', mode.id);
    setMode(mode.id);
    sfx.play('select');
  });
}
for (const b of document.querySelectorAll<HTMLElement>('.pos')) {
  b.addEventListener('click', () => {
    if (!s) return;
    const id = b.dataset.pos as 'creep' | 'walk' | 'bolt';
    s.posture = id === 'creep' ? Verb.Creep : id === 'bolt' ? Verb.Bolt : Verb.Step;
    setPosture(id);
    // A posture change re-prices every staged step, so the turn is rebuilt rather than left lying.
    staged = [];
    sfx.play('select');
    refreshView();
  });
}

on('#play', () => {
  sfx.unlock();
  if (!store.get('taught', false)) startTutorial();
  else startRun();
});
on('#howto', () => {
  sfx.unlock();
  openModal('How to play', `${HOW_TO}<p><button id="replay-teach" class="primary">Run the walkthrough</button></p>`);
  el('#replay-teach').addEventListener('click', () => {
    closeModal();
    startTutorial();
  });
});
on('#about', () => openModal('About', ABOUT));
on('#mute', () => {
  const m = !(store.get('muted', false) as boolean);
  store.set('muted', m);
  sfx.setMuted(m);
  const b = el<HTMLButtonElement>('#mute');
  b.textContent = m ? 'Sound off' : 'Sound on';
  b.setAttribute('aria-pressed', String(m));
});
on('#go', () => {
  sfx.unlock();
  commit();
});
on('#undo', () => {
  if (!staged.length) return;
  staged.pop();
  sfx.play('select');
  refreshView();
});
on('#teach-skip', () => {
  // One tap, from any step, into a live game. A player who already knows this must never be trapped.
  teaching = false;
  store.set('taught', true);
  el<HTMLElement>('#teach').hidden = true;
  startRun();
});
on('#slug', () => {
  if (!s || s.slugs <= 0) {
    sfx.play('deny');
    return;
  }
  slugArmed = !slugArmed;
  sfx.play('select');
  refreshView();
});
on('#pause', () =>
  openModal(
    'Paused',
    `${HOW_TO}<p><button id="quit" class="primary">Give up and go to the menu</button></p>`,
  ),
);
document.addEventListener('click', (e) => {
  const t = e.target as HTMLElement;
  if (t?.id === 'quit') {
    closeModal();
    showScreen('menu');
  }
});
on('#again', () => startRun(mode, ghost ? ghost.seed : (Math.random() * 0x7fffffff) | 0));
on('#share', share);
on('#tomenu', () => {
  showScreen('menu');
  bestLine(Object.fromEntries(MODE_IDS.map((id) => [id, store.get(`best:${id}`, 0) as number])));
});
el('.results-feedback').addEventListener('click', (e) =>
  window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }),
);

// The dismissal contract, all four ways out, every time.
on('#modal-close', closeModal);
el('#backdrop').addEventListener('pointerdown', () => {
  if (modalArmed()) closeModal();
});
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && modalOpen()) closeModal();
  if (!s || s.over || modalOpen()) return;
  const step = { ArrowLeft: -1, a: -1, ArrowRight: 1, d: 1, ArrowUp: -8, w: -8, ArrowDown: 8, s: 8 }[e.key];
  if (step !== undefined) {
    tryStage({ verb: s.posture, arg: s.pos + step === stagedPos() ? stagedPos() : stagedPos() + step });
    e.preventDefault();
  } else if (e.key === ' ' || e.key === 'Enter') {
    commit();
    e.preventDefault();
  } else if (e.key === 'z') {
    staged.pop();
    refreshView();
  } else if (e.key === '1') el<HTMLElement>('.pos[data-pos="creep"]').click();
  else if (e.key === '2') el<HTMLElement>('.pos[data-pos="walk"]').click();
  else if (e.key === '3') el<HTMLElement>('.pos[data-pos="bolt"]').click();
});

setMode(mode.id);
bestLine(Object.fromEntries(MODE_IDS.map((id) => [id, store.get(`best:${id}`, 0) as number])));
showScreen('menu');
el('#slug-n').textContent = String(SLUGS_CARRIED);
el('#slots').textContent = '•'.repeat(SLOTS);

const shared = fromLocation();
if (shared) openShared(shared);

// A cue that is played but never declared returns SILENTLY from the mixer, so the set is asserted at
// build time by tests/source-hygiene.test.ts rather than discovered by a player with the sound on.
export const DECLARED_CUES = CUES;
export const MODE_COUNT = Object.keys(MODES).length;
export const SLOT_COST = VERB_SLOTS;
export const EXITS_OF = validExits;
